import clsx from 'clsx';
import { Button } from 'components/Button/Button';
import PhoneNumberInput from 'components/PhoneNumberInput/PhoneNumberInput';
import useCountry from 'hooks/useCountry';
import { useGetVerificationStatusId } from 'hooks/useGetVerificationStatus';
import { useEffect, useState } from 'react';
import { useGetProfileQuery } from 'store/slices/profile-api';

interface Country {
  baseCurrencyCode?: string;
  code?: string;
  dialCode?: string;
  displayCurrencyCode?: string;
  name?: string;
  timeZone?: string;
}

const EnterDetailsView = ({
  onSubmitDetails,
  requestError,
  onChangePhoneNumber,
  onChangeCountryCode,
  phoneNumber,
  country,
  countryDialCode,
  onSetCountryDialCode,
  title,
  subtitle,
  buttonText,
  addMarginTop,
  lightMode,
}: {
  onSubmitDetails: Function;
  requestError?: string;
  onChangePhoneNumber: (value: string) => void;
  onChangeCountryCode: (value: string) => void;
  phoneNumber: string;
  country: string;
  countryDialCode: string;
  onSetCountryDialCode: (value: string) => void;
  title: string;
  subtitle: string;
  buttonText: string;
  addMarginTop?: boolean;
  lightMode?: boolean;
}) => {
  const [countryPicture, setCountryPicture] = useState<string>('');
  const countries = useCountry();
  const [isValid, setIsValid] = useState<boolean>(false);
  const verificationStatusId = useGetVerificationStatusId();
  const { data: profileData } = useGetProfileQuery();

  function getCountryDialCode(code: string) {
    const countryToUse = countries.find(
      x => x.code.toLowerCase() === code.toLowerCase(),
    );

    onSetCountryDialCode(countryToUse?.dialCode || '');
  }

  const number = verificationStatusId.completedSubmissionFields?.number?.trim();

  useEffect(() => {
    const countryCode = profileData?.countryCode;
    if (!countryCode) return;
    getCountryDialCode(countryCode);
    onChangePhoneNumber(number);
    onChangeCountryCode(countryCode);
    setCountryPicture(countryCode.toLowerCase());
  }, [profileData?.countryCode, number, country]);

  useEffect(() => {
    setIsValid(phoneNumber?.length > 3);
  }, [phoneNumber]);

  const onChangeCountry = (value: string) => {
    onChangeCountryCode(value);
    setCountryPicture(value.toLowerCase());
    getCountryDialCode(value);
  };

  const onRequestOTP = () => {
    if (isValid) onSubmitDetails();
  };

  return (
    <>
      <div className="flex flex-col items-start lg:items-center mb-6">
        <div
          className={clsx('font-semibold text-3xl leading-9 tracking-wide', {
            'lg:mt-20': addMarginTop,
            'text-black': lightMode,
            'text-white': !lightMode,
          })}>
          {title}
        </div>
        <div className="mt-2 font-normal text-lg leading-6 tracking-wide text-body-text">
          {subtitle}
        </div>
      </div>
      <PhoneNumberInput
        phoneNumber={phoneNumber}
        dialCode={countryDialCode}
        countries={countries}
        country={country}
        countryPicture={countryPicture}
        onChangeCountry={onChangeCountry}
        onChangeNumber={onChangePhoneNumber}
        className="lg:w-512"
        lightMode={lightMode}
      />

      {requestError && <div className="text-error mt-4">{requestError}</div>}

      <div className="max-w-lg sm:mx-auto sm:w-full text-center">
        <div className="mt-4 sm:mx-auto sm:w-full">
          <span className="block w-full rounded-md">
            <Button
              onClick={() => onRequestOTP()}
              full
              main
              disabled={!isValid}
              type="button">
              {buttonText}
            </Button>
          </span>
        </div>
      </div>
    </>
  );
};

export default EnterDetailsView;
